import * as React from 'react';
import { BrowserRouter, Route, Routes as RouterRoutes, useLocation, useNavigate } from 'react-router-dom';
import { CaseRunCreate } from './CaseRun/CaseRunCreate';
import { CaseCreate } from './Cases/CaseCreate';
import { CaseDetails } from './Cases/CaseDetails';
import { Cases } from './Cases/Cases';
import { AppRoutes } from './AppRoutes';
import { WorkQueue } from './WorkQueue/WorkQueue';
import { CaseRunPrintPage } from './CaseRun/Print/CaseRunPrintPage';
import { CaseRunDetails } from './CaseRun/Details/CaseRunDetails';
import { CaseRunCalculate } from './CaseRun/CaseRunCalculate';
import { CaseEdit } from './Cases/CaseEdit';
import { Kits } from './Kits/Kits';
import { RouteContent } from "./Common/RouteContent";
import { LabCreate } from './Labs/LabCreate';
import { LabEdit } from './Labs/LabEdit';
import { Labs } from './Labs/Labs';
import { LabDetailView } from './Labs/LabDetail';
import { Populations } from './Populations/Populations';
import { PopulationEditor } from './Populations/PopulationEditor';
import EvidenceDetails from './Evidence/EvidenceDetails';
import { EvidenceEdit } from './Evidence/EvidenceEdit';
import EvidenceCopy from './Evidence/EvidenceCopy';
import { SampleCreate } from './Samples/SampleCreate';
import { KeyboardUpload } from './Samples/KeyboardUpload';
import SampleDetails from './Samples/SampleDetails';
import { AppSettings } from './Config/AppSettings';
import { PopulationCreateLocal } from './Populations/PopulationCreateLocal';
import { PopulationDetails } from './Populations/PopulationDetails';
import { HomePage } from './Home/HomePage';
import { CustomPanel } from './Samples/CustomPanel';
import SampleCopy from './Samples/SampleCopy';
import { Profiles } from './Profiles/Profiles';
import { ProfilesSampleUpload } from './Profiles/ProfilesSampleUpload';
import { SampleImport } from './Samples/SampleImport';
import { SampleCalcRMP } from './Samples/SampleCalcRMP';
import { ProfilesEvidenceUpload } from './Profiles/ProfilesEvidenceUpload';
import { EvidenceImport } from './Evidence/EvidenceImport';
import { CaseRunCreateWithLabCaseSelect } from './CaseRun/CaseRunCreateWithLabCaseSelect';
import { UserProvider, useUser } from './Contexts/UserContext';
import EnrollmentForm from './Enrollments/Manage/EnrollmentForm';
import { Enrollments } from './Enrollments/Enrollments';
import { Toaster as HotToaster } from 'react-hot-toast';
import { Toaster } from "@/components/ui/toaster";
import { HelmetProvider } from 'react-helmet-async';
import { Users } from './Users/Users';
import { UserCreate } from './Users/UserCreate';
import { UserDetails } from './Users/UserDetails';
import { UserEditFunctional } from './Users/UserEdit';
import { Roles } from './Roles/Roles';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6'
// import { Routes as RouterRoutes, Route } from 'react-router-dom-v5-compat';
import {
    Login,
    Login2fa,
    LoginWithRecoveryCode,
    Register,
    ForgotPassword,
    ResetPassword,
    Lockout,
    ManageProfile,
    ManageChangePassword,
    ManageEmail,
    ManageTwoFactor,
    ManageEnableAuthenticator,
    ManageGenerateRecoveryCodes,
    ManagePersonalData
} from './Auth';
import { TranslationProvider } from './Contexts/TranslationContext';
import { ResendEmailConfirmation } from './Auth/ResendEmailConfirmation';
import { DevIndex } from './Dev/Index';
import { DialogProvider } from './Hooks/DialogContext';
import { TooltipProvider } from '@/components/ui/tooltip';
// import { CompatRoute, CompatRouter, Router } from 'react-router-dom-v5-compat';
import BPMenu from './Common/BPMenu';
import { BPFooter } from './Common/Footer/BPFooter';
import { TranslationProvider2 } from './Contexts/TranslationContext2';
import { Button, Container, Header, Segment } from 'semantic-ui-react';
import BPPageNotFound from './Common/BPPageNotFound';
import { SampleEdit } from './Samples/SampleEdit';
import { QueryClient, QueryClientProvider, QueryErrorResetBoundary } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import BPUnexpectedError from './Common/BPUnexpectedError';
import { BPButton } from '@/components/bp/bpbutton';
import { BPContainer } from '@/components/bp/bpcontainer';

export interface IAppProps {
    canCreateCase: boolean;
    canCreateUser: boolean;
    canCreateLab: boolean;
    canEditLab: boolean;
    canDeleteCase: boolean;
    canManagePopulations: boolean;
}

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            // queryFn: getQueryFn({ on401: "throw" }),
            refetchInterval: false,
            refetchOnWindowFocus: false,
            staleTime: 0,
            // staleTime: 10 * 1000, // 10 seconds
            retry: false,
            throwOnError: true,
        },
        mutations: {
            retry: false,
        },

    },
});

export const App: React.FunctionComponent<IAppProps> = (props) => {
    return (
        <QueryClientProvider client={queryClient}>
            <UserProvider>
                <TranslationProvider>
                    <TranslationProvider2>
                        {/* <DialogProvider> */}
                        <HelmetProvider>
                            <QueryErrorResetBoundary>
                                {({ reset }) => (
                                    <ErrorBoundary
                                        onReset={reset}
                                        fallbackRender={({ resetErrorBoundary }) => (
                                            <BPContainer>
                                                <Segment placeholder style={{ textAlign: "center", marginTop: '20vh' }}>
                                                    <Header>Unexpected Error</Header>
                                                    <BPButton onClick={() => resetErrorBoundary()}>Try again</BPButton>
                                                </Segment>
                                            </BPContainer>
                                        )}>

                                        <BrowserRouter basename={`/`} future={{
                                            v7_relativeSplatPath: true,
                                            v7_startTransition: true
                                        }}>
                                            <DialogProvider>
                                                <NuqsAdapter>
                                                    <InnerApp {...props}></InnerApp>
                                                </NuqsAdapter>
                                                {/* </CompatRouter> */}
                                            </DialogProvider>
                                        </BrowserRouter>
                                    </ErrorBoundary>
                                )}
                            </QueryErrorResetBoundary>


                        </HelmetProvider>
                        {/* </DialogProvider> */}
                    </TranslationProvider2>
                </TranslationProvider >
            </UserProvider>
        </QueryClientProvider>

    );
};

const InnerApp = (props) => {
    const user = useUser();
    const navigate = useNavigate();
    const location = useLocation();
    if (!user?.user)
        return <div></div>;
    return <>
        <TooltipProvider>
            {user?.user?.roles && <BPMenu roles={user.user.roles} userName={user.user.details.name} isAllowed={(policy) => user?.user?.policies?.includes(policy)} onLogout={async () => {
                await user.logoutAndRedirectToLogin();
            }} />}

            <BPContainer style={{ flex: 1 }}>
                {/* <Toaster /> */}
                <HotToaster position="bottom-center" />
                <RoutesSwitch {...props} />
            </BPContainer>
            <BPFooter />
        </TooltipProvider>
    </>
}

const RoutesSwitch = (props) => {
    return <RouterRoutes>
        {/* Authentication routes */}
        <Route path={AppRoutes.Login} element={<Login />} />
        <Route path={AppRoutes.Login2fa} element={<Login2fa />} />
        <Route path={AppRoutes.LoginRecovery} element={<LoginWithRecoveryCode />} />
        <Route path={AppRoutes.Register} element={<Register />} />
        <Route path={AppRoutes.ForgotPassword} element={<ForgotPassword />} />
        <Route path={AppRoutes.ResetPassword} element={<ResetPassword />} />
        <Route path={AppRoutes.Lockout} element={<Lockout />} />
        <Route path={AppRoutes.ResendEmailConfirmation} element={<ResendEmailConfirmation />} />

        {/* Account management routes */}
        <Route path={AppRoutes.ManageProfile} element={<ManageProfile />} />
        <Route path={AppRoutes.ManageEmail} element={<ManageEmail />} />
        <Route path={AppRoutes.ManageChangePassword} element={<ManageChangePassword />} />
        <Route path={AppRoutes.ManageTwoFactor} element={<ManageTwoFactor />} />
        <Route path={AppRoutes.ManageEnableAuthenticator} element={<ManageEnableAuthenticator />} />
        <Route path={AppRoutes.ManageGenerateRecoveryCodes} element={<ManageGenerateRecoveryCodes />} />
        <Route path={AppRoutes.ManagePersonalData} element={<ManagePersonalData />} />

        {/* Development routes */}
        <Route path={AppRoutes.DevIndex} element={<DevIndex />} />

        {/* Application routes */}
        <Route path={AppRoutes.Home} element={<HomePage {...props} />} />
        <Route path={AppRoutes.AppSettings} element={<AppSettings {...props} />} />

        <Route path={AppRoutes.Lab} element={<Labs {...props} />} />
        {/* <Route path={AppRoutes.LabDetails + '/*'} element={<RouteContent render={(params) => <LabDetailView labId={parseInt(params.params.labId)} {...props} />} />}/> */}
        <Route path={AppRoutes.LabDetails}>
            <Route path="*" index element={<RouteContent render={(params) => <LabDetailView labId={parseInt(params.params.labId)} {...props} />} />} />
        </Route>

        <Route path={AppRoutes.LabCreate} element={<LabCreate />} />
        <Route path={AppRoutes.LabEdit} element={<RouteContent render={(params) => <LabEdit labId={parseInt(params.params.labId)} {...props} />} />} />

        <Route path={AppRoutes.User} element={<Users {...props} />} />
        <Route path={AppRoutes.UserCreate} element={<UserCreate {...props} />} />
        <Route path={AppRoutes.UserEdit} element={<RouteContent render={(router) => <UserEditFunctional userId={router.params.userId} {...props} />} />} />
        <Route path={AppRoutes.UserDetails} element={<RouteContent render={(router) => <UserDetails userId={router.params.userId} {...props} />} />} />

        <Route path={AppRoutes.Kit}>
            <Route path={'*'} index element={<RouteContent render={(router) => <Kits baseUrl={router.params.url} />} />} />
        </Route>
        <Route path={AppRoutes.WorkQueue} element={<RouteContent render={() => <WorkQueue />} />} />
        <Route path={AppRoutes.Case} element={<RouteContent render={() => <Cases canCreateCase={props.canCreateCase} />} />} />
        <Route path={AppRoutes.CaseCreate} element={<RouteContent render={() => <CaseCreate />} />} />
        <Route path={AppRoutes.CaseEdit} element={<RouteContent render={p => <CaseEdit caseId={parseInt(p.params.caseId)} labId={parseInt(p.params.labId)} />} />} />
        <Route path={AppRoutes.CaseDetails} element={<RouteContent render={p => <CaseDetails caseId={parseInt(p.params.caseId)} labId={parseInt(p.params.labId)} />} />} />

        <Route path={AppRoutes.CaseRunCreate}>
            <Route path={'*'} index element={<RouteContent render={p => {
                let evidenceId = parseInt(new URLSearchParams(p.location.search).get('evidenceId'));
                if (isNaN(evidenceId))
                    evidenceId = null;
                let ednaTestId = parseInt(new URLSearchParams(p.location.search).get('ednaTestId'));
                if (isNaN(ednaTestId))
                    ednaTestId = null;

                return <CaseRunCreate caseId={parseInt(p.params.caseId)} labId={parseInt(p.params.labId)} runType={p.params.runType} aic={false} evidenceId={evidenceId} ednaTestId={ednaTestId} />;
            }} />} />
        </Route>
        <Route path={AppRoutes.CaseRunCreateWithLabSelect}>
            <Route path={'*'} index element={<RouteContent render={p => {
                return <CaseRunCreateWithLabCaseSelect runType={p.params.runType}
                    baseUrl={p.params.url} />;
            }} />} />
        </Route>

        <Route path={AppRoutes.CaseRunPrint} element={<RouteContent render={router => <CaseRunPrintPage labId={parseInt(router.params.labId)} caseId={parseInt(router.params.caseId)} caseRunId={parseInt(router.params.caseRunId)} />} />} />
        <Route path={AppRoutes.CaseRunDetails}>
            <Route path={'*'} index element={<RouteContent render={router => <CaseRunDetails labId={parseInt(router.params.labId)} caseId={parseInt(router.params.caseId)} caseRunId={parseInt(router.params.caseRunId)} />} />} />
            {/* <Route path={'calculations'}>
                <Route path={'*'} index element={<RouteContent render={router => <CaseRunDetails labId={parseInt(router.params.labId)} caseId={parseInt(router.params.caseId)} caseRunId={parseInt(router.params.caseRunId)} />} />}/>
            </Route> */}
        </Route>
        {/* <Route path={Routes.CaseRunDetailsResults} element={<RouteContent render={router => <CaseRunDetails labId={parseInt(router.params.labId)} caseId={parseInt(router.params.caseId)} caseRunId={parseInt(router.params.caseRunId)} />} />} /> */}
        {/* <Route path={Routes.CaseRunCalculationDetails + '/*'} element={<RouteContent render={router => <CaseRunDetails labId={parseInt(router.params.labId)} caseId={parseInt(router.params.caseId)} caseRunId={parseInt(router.params.caseRunId)} />} />} /> */}
        <Route path={AppRoutes.CaseRunCalculate}>
            <Route path={'*'} index element={<RouteContent render={router => {
                return <CaseRunCalculate labId={parseInt(router.params.labId)} caseId={parseInt(router.params.caseId)} caseRunId={parseInt(router.params.caseRunId)} runType={router.params.runType}
                    baseUrl={router.params.url} routerProps={router} />;
            }} />} />

        </Route>

        <Route path={AppRoutes.CaseRunClone}>
            <Route path={'*'} index element={<RouteContent render={route =>
                <CaseRunCreate copyFrom={parseInt(route.params.oldRunId)} labId={parseInt(route.params.labId)} caseId={parseInt(route.params.caseId)} runType={route.params.runType} aic={false} />} />} />
        </Route>


        <Route path={AppRoutes.Populations} element={<Populations {...props} />} />
        <Route path={AppRoutes.PopulationUpload} element={<RouteContent render={() => <PopulationEditor {...props} newPopulation={true} populationId={null} />} />} />
        <Route path={AppRoutes.PopulationEdit} element={<RouteContent render={(router) => <PopulationEditor {...props} newPopulation={false} populationId={parseInt(router.params.populationId)} />} />} />
        <Route path={AppRoutes.PopulationCreateLocal} element={<RouteContent render={(router) => <PopulationCreateLocal {...props} />} />} />
        <Route path={AppRoutes.PopulationDetail} element={<RouteContent render={(route) => <PopulationDetails {...props} populationId={parseInt(route.params.populationId)} />} />} />

        <Route path={AppRoutes.EvidenceDetails} element={<RouteContent render={(route) => <EvidenceDetails {...props} id={parseInt(route.params.evidenceId)} labId={parseInt(route.params.labId)} caseId={parseInt(route.params.caseId)} />} />} />
        <Route path={AppRoutes.EvidenceEdit} element={<RouteContent render={(route) => <EvidenceEdit {...props} genGraph={false} evidenceId={parseInt(route.params.evidenceId)} labId={parseInt(route.params.labId)} caseId={parseInt(route.params.caseId)} />} />} />
        <Route path={AppRoutes.EvidenceUpload} element={<RouteContent render={(route) => <EvidenceEdit {...props} genGraph={true} evidenceId={null} labId={parseInt(route.params.labId)} caseId={parseInt(route.params.caseId)} />} />} />
        <Route path={AppRoutes.EvidenceCreateCopy} element={<RouteContent render={(route) => <EvidenceCopy {...props} evidenceId={parseInt(route.params.evidenceId)} labId={parseInt(route.params.labId)} caseId={parseInt(route.params.caseId)} />} />} />
        <Route path={AppRoutes.EvidenceImport} element={<RouteContent render={(route) => <EvidenceImport {...props} labId={parseInt(route.params.labId)} caseId={parseInt(route.params.caseId)} />} />} />

        <Route path={AppRoutes.SampleDetails} element={<RouteContent render={(route) => <SampleDetails {...props} id={parseInt(route.params.sampleId)} labId={parseInt(route.params.labId)} caseId={parseInt(route.params.caseId)} />} />} />
        <Route path={AppRoutes.SampleEdit} element={<RouteContent render={(route) => <SampleEdit {...props} sampleId={parseInt(route.params.sampleId)} labId={parseInt(route.params.labId)} caseId={parseInt(route.params.caseId)} />} />} />
        <Route path={AppRoutes.SampleUpload} element={<RouteContent render={(route) => <SampleCreate {...props} labId={parseInt(route.params.labId)} caseId={parseInt(route.params.caseId)} />} />} />
        <Route path={AppRoutes.SampleKeyboardUpload} element={<RouteContent render={(route) => <KeyboardUpload {...props} labId={parseInt(route.params.labId)} caseId={parseInt(route.params.caseId)} />} />} />
        <Route path={AppRoutes.SampleCreateCopy} element={<RouteContent render={(route) => <SampleCopy {...props} sampleId={parseInt(route.params.sampleId)} labId={parseInt(route.params.labId)} caseId={parseInt(route.params.caseId)} />} />} />
        <Route path={AppRoutes.SampleImport} element={<RouteContent render={(route) => <SampleImport {...props} labId={parseInt(route.params.labId)} caseId={parseInt(route.params.caseId)} />} />} />
        <Route path={AppRoutes.SampleCalcRMP} element={<RouteContent render={(route) => <SampleCalcRMP {...props} labId={parseInt(route.params.labId)} caseId={parseInt(route.params.caseId)} sampleId={parseInt(route.params.sampleId)} />} />} />

        <Route path={AppRoutes.SampleDetailsWithoutCase} element={<RouteContent render={(route) => <SampleDetails {...props} id={parseInt(route.params.sampleId)} labId={parseInt(route.params.labId)} />} />} />
        <Route path={AppRoutes.SampleCalcRMPWithoutCase} element={<RouteContent render={(route) => <SampleCalcRMP {...props} labId={parseInt(route.params.labId)} caseId={parseInt(route.params.caseId)} sampleId={parseInt(route.params.sampleId)} />} />} />
        <Route path={AppRoutes.SampleEditWithoutCase} element={<RouteContent render={(route) => <SampleEdit {...props} sampleId={parseInt(route.params.sampleId)} labId={parseInt(route.params.labId)} />} />} />
        {/* <Route path={AppRoutes.SampleUploadWithoutCase} element={<RouteContent render={(route) => <SampleCreate {...props} labId={parseInt(route.params.labId)} />} />} /> */}
        <Route path={AppRoutes.SampleCreateCopyWithoutCase} element={<RouteContent render={(route) => <SampleCopy {...props} sampleId={parseInt(route.params.sampleId)} labId={parseInt(route.params.labId)} />} />} />

        <Route path={AppRoutes.CaseCustomPanelUpload} element={<RouteContent render={(route) => <CustomPanel {...props} labId={parseInt(route.params.labId)} caseId={parseInt(route.params.caseId)} />} />} />
        <Route path={AppRoutes.CaseCustomPanelEdit} element={<RouteContent render={(route) => <CustomPanel {...props} panelId={parseInt(route.params.panelId)} labId={parseInt(route.params.labId)} caseId={parseInt(route.params.caseId)} />} />} />

        <Route path={AppRoutes.Profiles} element={<RouteContent render={(route) => <Profiles {...props} />} />} />
        <Route path={AppRoutes.ProfilesSampleEdit} element={<RouteContent render={(route) => <ProfilesSampleUpload {...props} sampleId={parseInt(route.params.sampleId)} labId={parseInt(route.params.labId)} caseId={parseInt(route.params.caseId)} />} />} />
        <Route path={AppRoutes.ProfilesSampleUpload} element={<RouteContent render={(route) => <ProfilesSampleUpload {...props} />} />} />
        <Route path={AppRoutes.ProfilesEvidenceUpload} element={<RouteContent render={(route) => <ProfilesEvidenceUpload {...props} />} />} />

        <Route path={AppRoutes.ProfilesMetadataCreate}>
            <Route path={'*'} index element={<RouteContent render={(route) => <EnrollmentForm key='mcreate' {...props} />} />} />
        </Route>
        {/* <Route path={Routes.ProfilesMetadataCreateBio} element={<RouteContent render={(route) => <EnrollmentForm key='mcreate' {...props} />} />} />
        <Route path={Routes.ProfilesMetadataCreatePhysical} element={<RouteContent render={(route) => <EnrollmentForm key='mcreate' {...props} />} />} />
        <Route path={Routes.ProfilesMetadataCreateContact} element={<RouteContent render={(route) => <EnrollmentForm key='mcreate' {...props} />} />} />
        <Route path={Routes.ProfilesMetadataCreateIdentification} element={<RouteContent render={(route) => <EnrollmentForm key='mcreate' {...props} />} />} />
        <Route path={Routes.ProfilesMetadataCreateAdditional} element={<RouteContent render={(route) => <EnrollmentForm key='mcreate' {...props} />} />} />
        <Route path={Routes.ProfilesMetadataCreateEmployment} element={<RouteContent render={(route) => <EnrollmentForm key='mcreate' {...props} />} />} /> */}

        <Route path={AppRoutes.ProfilesMetadataEdit}>
            <Route path={'*'} index element={<RouteContent render={(route) => <EnrollmentForm key='medit' {...props} metaId={parseInt(route.params.metaId)} />} />} />

        </Route>
        {/* <Route path={Routes.ProfilesMetadataEditBio} element={<RouteContent render={(route) => <EnrollmentForm key='medit' {...props} metaId={parseInt(route.params.metaId)} />} />} />
        <Route path={Routes.ProfilesMetadataEditPhysical} element={<RouteContent render={(route) => <EnrollmentForm key='medit' {...props} metaId={parseInt(route.params.metaId)} />} />} />
        <Route path={Routes.ProfilesMetadataEditContact} element={<RouteContent render={(route) => <EnrollmentForm key='medit' {...props} metaId={parseInt(route.params.metaId)} />} />} />
        <Route path={Routes.ProfilesMetadataEditIdentification} element={<RouteContent render={(route) => <EnrollmentForm key='medit' {...props} metaId={parseInt(route.params.metaId)} />} />} />
        <Route path={Routes.ProfilesMetadataEditAdditional} element={<RouteContent render={(route) => <EnrollmentForm key='medit' {...props} metaId={parseInt(route.params.metaId)} />} />} />
        <Route path={Routes.ProfilesMetadataEditEmployment} element={<RouteContent render={(route) => <EnrollmentForm key='medit' {...props} metaId={parseInt(route.params.metaId)} />} />} /> */}

        <Route path={AppRoutes.ProfilesMetadata}>
            <Route path={'*'} index element={<RouteContent render={(route) => <Enrollments {...props} />} />} />
        </Route>
        {/* <Route path={Routes.ProfilesMetadataSearch} element={<RouteContent render={(route) => <Enrollments {...props} />} />} /> */}

        <Route path={AppRoutes.Roles} element={<RouteContent render={(route) => <Roles {...props} />} />} />

        <Route path='*' element={<BPPageNotFound />} />

    </RouterRoutes>
};